import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, XCircle, Info, AlertCircle } from 'lucide-react';
import {
  DataPurgePolicy,
  UpdateDataPurgePolicyRequest,
  POLICY_KEY_LABELS,
  PURGE_APPLICATION_NAME,
  PURGE_MESSAGE_PREFIX
} from '../../types/DataPurgePolicy';
import { dataPurgeApi } from '../../services/dataPurgeApi';
import { ApplicationLog } from '../../types/ApplicationLog';
import { logApi } from '../../services/logApi';

type SubTab = 'config' | 'history';

const formatTimestamp = (value: string): string => {
  const date = new Date(value);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const DataPurgeManagement: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>('config');

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Datapurge</h2>
      </div>

      {/* Sub-navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-6" aria-label="Datapurge sub-tabs">
          <button
            onClick={() => setSubTab('config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              subTab === 'config'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Konfigurasjon
          </button>
          <button
            onClick={() => setSubTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              subTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Kjørehistorikk
          </button>
        </nav>
      </div>

      {subTab === 'config' ? <PurgeConfig /> : <PurgeHistory />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Konfigurasjon — read/update av WMS_DataPurgePolicy
// ---------------------------------------------------------------------------
const PurgeConfig: React.FC = () => {
  const [policies, setPolicies] = useState<DataPurgePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateDataPurgePolicyRequest | null>(null);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dataPurgeApi.getPolicies();
      setPolicies(data);
    } catch (err) {
      console.error('Error fetching purge policies:', err);
      setError('Feil ved henting av purge-policyer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleEditStart = (p: DataPurgePolicy) => {
    setEditingKey(p.policyKey);
    setEditForm({
      retentionDays: p.retentionDays,
      isEnabled: p.isEnabled,
      dryRun: p.dryRun,
      description: p.description || ''
    });
  };

  const handleEditCancel = () => {
    setEditingKey(null);
    setEditForm(null);
  };

  const handleEditSave = async () => {
    if (!editingKey || !editForm) return;
    try {
      await dataPurgeApi.updatePolicy(editingKey, editForm);
      setEditingKey(null);
      setEditForm(null);
      setSuccessMessage('Policy ble oppdatert');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchPolicies();
    } catch (err) {
      console.error('Error updating purge policy:', err);
      setError('Feil ved oppdatering av policy');
    }
  };

  if (loading && policies.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Laster policyer...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold">&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md text-sm flex gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <p><strong>Tørrkjøring (DryRun):</strong> når avkrysset logger workeren kun «ville slettet N» uten å slette noe. Skru av for skarp sletting.</p>
          <p className="mt-1">Policy-nøklene er kode-bundet til workeren og kan derfor ikke opprettes/slettes herfra — kun konfigureres.</p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Oppbevaring (dager)</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Aktiv</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Tørrkjøring</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sist endret</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {policies.map((p) => (
              <tr key={p.policyKey} className={`hover:bg-gray-50 ${!p.isEnabled ? 'bg-gray-50 opacity-70' : ''}`}>
                {editingKey === p.policyKey && editForm ? (
                  // Edit mode
                  <>
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{POLICY_KEY_LABELS[p.policyKey] || p.policyKey}</div>
                      <div className="text-xs font-mono text-gray-500">{p.policyKey}</div>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="number"
                        min={0}
                        value={editForm.retentionDays}
                        onChange={(e) => setEditForm({ ...editForm, retentionDays: parseInt(e.target.value) || 0 })}
                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={editForm.isEnabled}
                        onChange={(e) => setEditForm({ ...editForm, isEnabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={editForm.dryRun}
                        onChange={(e) => setEditForm({ ...editForm, dryRun: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{formatTimestamp(p.modifiedDate)}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button onClick={handleEditSave} className="text-green-600 hover:text-green-800 text-sm font-medium">Lagre</button>
                      <button onClick={handleEditCancel} className="text-gray-600 hover:text-gray-800 text-sm font-medium">Avbryt</button>
                    </td>
                  </>
                ) : (
                  // View mode
                  <>
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{POLICY_KEY_LABELS[p.policyKey] || p.policyKey}</div>
                      <div className="text-xs font-mono text-gray-500">{p.policyKey}</div>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {p.description || <span className="text-gray-300 italic">Ingen beskrivelse</span>}
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-900 font-medium">{p.retentionDays}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${p.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                        {p.isEnabled ? 'Aktiv' : 'Av'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${p.dryRun ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                        {p.dryRun ? 'Tørrkjøring' : 'Skarp'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatTimestamp(p.modifiedDate)}
                      {p.modifiedBy && <span className="text-gray-400"> · {p.modifiedBy}</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleEditStart(p)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Rediger</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {policies.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">Ingen purge-policyer funnet i WMS_DataPurgePolicy</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kjørehistorikk — utvalg fra master ApplicationLogs (workerens egne logglinjer)
// ---------------------------------------------------------------------------
const PurgeHistory: React.FC = () => {
  const [logs, setLogs] = useState<ApplicationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [levelFilter, setLevelFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const [logsData, count] = await Promise.all([
        logApi.getLogs(pageSize, pageNumber, levelFilter || undefined, undefined, undefined, PURGE_APPLICATION_NAME, PURGE_MESSAGE_PREFIX),
        logApi.getLogCount(levelFilter || undefined, undefined, undefined, PURGE_APPLICATION_NAME, PURGE_MESSAGE_PREFIX)
      ]);
      setLogs(logsData);
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching purge logs:', err);
      setError('Kunne ikke hente kjørehistorikk');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, levelFilter]);

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'information':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Purge-linjer fra tjenesten (<span className="font-mono">{PURGE_APPLICATION_NAME}</span>, meldinger som starter med «{PURGE_MESSAGE_PREFIX}»). Én linje per tenant per policy.
        </p>
        <div className="flex gap-2">
          <select
            value={levelFilter}
            onChange={(e) => { setLevelFilter(e.target.value); setPageNumber(1); }}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Alle nivåer</option>
            <option value="Error">Error</option>
            <option value="Warning">Warning</option>
            <option value="Information">Information</option>
          </select>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </button>
        </div>
      </div>

      {error && (
        <div className="p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
      )}

      <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Nivå</th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">Tidspunkt</th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">Melding</th>
                <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">Maskin</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-xs">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {getLevelIcon(log.level)}
                      <span className="font-medium">{log.level.substring(0, 4)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1 whitespace-nowrap text-gray-600 font-mono">{formatTimestamp(log.timeStamp)}</td>
                  <td className="px-3 py-1 text-gray-900">{log.message}</td>
                  <td className="px-3 py-1 whitespace-nowrap text-gray-600">{log.machineName?.split('-')[0] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-4 text-gray-500 text-sm">Ingen kjørehistorikk funnet ennå</div>
        )}

        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {(pageNumber - 1) * pageSize + 1}-{Math.min(pageNumber * pageSize, totalCount)} av {totalCount}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                disabled={pageNumber === 1}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ◀
              </button>
              <span className="px-2 py-1 text-xs">{pageNumber}/{totalPages}</span>
              <button
                onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                disabled={pageNumber === totalPages}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ▶
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataPurgeManagement;
